<template>
  <div class="cruise-comparison">
    <h2>Cruise Line Comparison</h2>
    
    <div class="comparison-filters">
      <div class="filter-group">
        <label for="destination-compare">Destination:</label>
        <select id="destination-compare" v-model="selectedDestination">
          <option value="">All Destinations</option>
          <option v-for="dest in destinations" :key="dest.id || dest" :value="dest.id || dest">{{ dest.label || dest }}</option>
        </select>
      </div>
      
      <div class="filter-group">
        <label for="duration-compare">Duration:</label>
        <select id="duration-compare" v-model="selectedDuration">
          <option value="">Any Length</option>
          <option value="1-5">1-5 Days</option>
          <option value="6-9">6-9 Days</option>
          <option value="10-14">10-14 Days</option>
          <option value="15+">15+ Days</option>
        </select>
      </div>
      
      <div class="filter-group">
        <label for="cabin-type">Cabin Type:</label>
        <select id="cabin-type" v-model="selectedCabinType">
          <option value="Interior">Interior</option>
          <option value="Oceanview">Oceanview</option>
          <option value="Balcony">Balcony</option>
          <option value="Suite">Suite</option>
        </select>
      </div>
      
      <button type="button" @click="generateComparison" class="compare-button" :disabled="loading">
        Compare
      </button>
    </div>
    
    <div v-if="loading" class="loading-indicator app-loading">
      <div class="spinner app-spinner"></div>
      <p>Generating comparison...</p>
    </div>

    <div v-else-if="error" class="app-error-state">
      <p>{{ error }}</p>
      <button type="button" class="app-retry-btn" @click="generateComparison">Try Again</button>
    </div>
    
    <div v-else-if="comparisonData.length > 0" class="comparison-results">
      <h3 v-if="pricedComparison.length">Average Price by Cruise Line</h3>
      <p class="comparison-subtitle">{{ getComparisonSubtitle() }}</p>
      <p v-if="countFilterNote" class="comparison-omitted">{{ countFilterNote }}</p>
      <p v-if="omittedSummary" class="comparison-omitted">{{ omittedSummary }}</p>
      
      <div v-if="pricedComparison.length" class="chart-container" :class="{ 'chart-horizontal': isPhone }">
        <bar-chart v-if="chartData" :chart-data="chartData" :options="chartOptions"></bar-chart>
      </div>
      
      <!-- Desktop table -->
      <div class="comparison-table desktop-table">
        <table>
          <thead>
            <tr>
              <th>Cruise Line</th>
              <th>Avg. Price</th>
              <th>Min Price</th>
              <th>Max Price</th>
              <th>Cruises <span class="count-header-note">(this cabin)</span></th>
              <th>Partner Offers</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in comparisonData" :key="item.cruiseLine">
              <td>{{ item.cruiseLine }}</td>
              <td v-if="item.omissionReason" colspan="3" class="omission-reason">{{ item.omissionReason }}</td>
              <td v-if="!item.omissionReason">{{ formatMoney(item.avgPrice) }}</td>
              <td v-if="!item.omissionReason">{{ formatMoney(item.minPrice) }}</td>
              <td v-if="!item.omissionReason">{{ formatMoney(item.maxPrice) }}</td>
              <td>
                <template v-if="item.omissionReason">{{ item.countNote || item.catalogCount }}</template>
                <template v-else>
                  {{ item.count }}
                  <span v-if="item.countNote" class="count-note">{{ item.countNote }}</span>
                </template>
              </td>
              <td>
                <a v-affiliate-link
                  v-if="canBook(item.cruiseLine)"
                  class="cs-btn-book-secondary compare-affiliate-cta"
                  :href="affiliateUrl(item)"
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  :aria-label="`${compareCta(item)}: ${item.cruiseLine} (opens CruiseDirect in a new tab)`"
                  @click="handleAffiliateClick(item)"
                >{{ compareCta(item) }} ↗</a>
                <span
                  v-else
                  class="cs-btn-book-disabled compare-book-quiet"
                  role="status"
                  title="Direct booking is not available on this site"
                >Not Bookable Here</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Phone card-stack (CP-26) -->
      <div class="comparison-cards" aria-label="Comparison results">
        <article v-for="item in comparisonData" :key="'card-' + item.cruiseLine" class="line-card">
          <h4 class="line-card-title">{{ item.cruiseLine }}</h4>
          <p v-if="item.omissionReason" class="omission-reason">{{ item.omissionReason }}</p>
          <dl v-else class="line-card-stats">
            <div><dt>Avg. Price</dt><dd>{{ formatMoney(item.avgPrice) }}</dd></div>
            <div><dt>Min</dt><dd>{{ formatMoney(item.minPrice) }}</dd></div>
            <div><dt>Max</dt><dd>{{ formatMoney(item.maxPrice) }}</dd></div>
            <div>
              <dt>Cruises</dt>
              <dd>
                {{ item.count }}
                <span v-if="item.countNote" class="count-note">{{ item.countNote }}</span>
              </dd>
            </div>
          </dl>
          <p v-if="item.omissionReason" class="line-card-catalog">{{ item.countNote || (item.catalogCount + ' sailings in catalog') }}</p>
          <div class="line-card-actions">
            <router-link
              :to="{ path: '/cruises', query: { cruise_line: item.cruiseLine } }"
              class="line-card-explore"
            >
              <i class="fas fa-ship" aria-hidden="true"></i>
              Explore Line Cruises
            </router-link>
            <a v-affiliate-link
              v-if="canBook(item.cruiseLine)"
              class="cs-btn-book-secondary line-card-affiliate"
              :href="affiliateUrl(item)"
              target="_blank"
              rel="noopener noreferrer sponsored"
              :aria-label="`${compareCta(item)}: ${item.cruiseLine} (opens in a new tab)`"
              @click="handleAffiliateClick(item)"
            >{{ compareCta(item) }} ↗</a>
            <span
              v-else
              class="cs-btn-book-disabled line-card-affiliate compare-book-quiet"
              role="status"
              title="Direct booking is not available on this site"
            >Not Bookable Here</span>
          </div>
        </article>
      </div>

      <p v-if="affiliateEnabled" class="comparison-affiliate-note">
        <i class="fas fa-info-circle"></i>
        {{ compareOnNote }}
        <router-link to="/disclosure">Full Disclosure</router-link>
      </p>
      <p v-else class="comparison-affiliate-note">
        <i class="fas fa-info-circle"></i>
        Rates displayed are catalog averages. Direct booking is not available on this site.
        <router-link to="/disclosure">Full Disclosure</router-link>
      </p>
    </div>
    
    <div v-else-if="hasAttemptedComparison" class="no-results app-empty-state">
      <p>No comparison data available for the selected criteria. Try different filters.</p>
    </div>
  </div>
</template>

<script>
import { labelForDestinationId, normalizeCanonicalDestinations } from '@/utils/canonicalDestinations';
import { formatCompareMoney } from '@/utils/formatMoney';
import apiService from '@/services/api';
import { affiliateState, canBookLine } from '@/utils/affiliateFlag';
import { COMPARE_ON_NOTE, tierCtaLabel } from '@/utils/bookingTierCopy';
import BarChart from '@/components/BarChart.vue';
import { notifyAffiliateClick } from '@/utils/affiliateBus';

const TEAL_SEQ = ['#E8F3F4', '#A8D0D4', '#5FA8B0', '#0B5B63', '#083F45'];

function tealSequential(n) {
  if (n <= 0) return [];
  if (n === 1) return [TEAL_SEQ[3]];
  const colors = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const idx = t * (TEAL_SEQ.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, TEAL_SEQ.length - 1);
    const f = idx - lo;
    colors.push(lerpHex(TEAL_SEQ[lo], TEAL_SEQ[hi], f));
  }
  return colors;
}

function lerpHex(a, b, t) {
  const pa = hexToRgb(a);
  const pb = hexToRgb(b);
  const r = Math.round(pa.r + (pb.r - pa.r) * t);
  const g = Math.round(pa.g + (pb.g - pa.g) * t);
  const bch = Math.round(pa.b + (pb.b - pa.b) * t);
  return `rgba(${r}, ${g}, ${bch}, 0.85)`;
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
}

export default {
  components: {
    BarChart
  },
  data() {
    return {
      destinations: [],
      selectedDestination: '',
      selectedDuration: '',
      selectedCabinType: 'Interior',
      comparisonData: [],
      loading: false,
      error: null,
      hasAttemptedComparison: false,
      chartData: null,
      isPhone: false,
      mediaQuery: null
    };
  },
  computed: {
    compareOnNote() {
      return COMPARE_ON_NOTE;
    },
    // CP-142: Compare CTAs follow the single runtime flag from /api/config.
    affiliateEnabled() {
      return affiliateState.enabled;
    },
    pricedComparison() {
      return this.comparisonData.filter(item => !item.omissionReason && item.avgPrice != null);
    },
    omittedSummary() {
      const omitted = this.comparisonData.filter(item => item.omissionReason);
      if (!omitted.length) return '';
      const parts = omitted.map(item => `${item.cruiseLine} (${item.omissionReason})`);
      return `Not included in the averages: ${parts.join('; ')}.`;
    },
    countFilterNote() {
      const filtered = this.comparisonData.some(
        item => !item.omissionReason && item.catalogCount > item.count
      );
      if (!filtered) return '';
      return 'Cruise counts are sailings with a recorded fare for this cabin. A smaller count when the cabin type changes is that filter: the other sailings in the catalog have no usable fare for the cabin. Lines with no usable fare show how many sailings are in the catalog.';
    },
    chartOptions() {
      const ink = '#1A2330';
      const muted = '#5B6570';
      const horizontal = this.isPhone;
      return {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(26, 35, 48, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            callbacks: {
              label(context) {
                return formatCompareMoney(context.raw);
              }
            }
          }
        },
        scales: horizontal
          ? {
              x: {
                beginAtZero: true,
                ticks: {
                  color: muted,
                  callback(value) {
                    return formatCompareMoney(value, 0);
                  }
                },
                grid: { color: 'rgba(217, 226, 228, 0.8)' }
              },
              y: {
                ticks: { color: ink, font: { size: 12, weight: '500' } },
                grid: { display: false }
              }
            }
          : {
              x: {
                ticks: { color: ink, font: { size: 12 } },
                grid: { display: false }
              },
              y: {
                beginAtZero: true,
                ticks: {
                  color: muted,
                  callback(value) {
                    return formatCompareMoney(value, 0);
                  }
                },
                grid: { color: 'rgba(217, 226, 228, 0.8)' }
              }
            }
      };
    }
  },
  mounted() {
    this.fetchDestinations();
    // CP-85: auto-run defaults (Interior / All Destinations / Any Length)
    // on first paint so /comparison is not empty until Compare is clicked.
    // Manual Compare still re-runs after filter changes.
    this.generateComparison();
    this.mediaQuery = window.matchMedia('(max-width: 640px)');
    this.isPhone = this.mediaQuery.matches;
    this.onMediaQueryChange = (e) => {
      // chartOptions already rebuilds the axis. Replacing chartData here
      // drew the chart twice and Chart.js lost the canvas.
      this.isPhone = e.matches;
    };
    if (this.mediaQuery.addEventListener) this.mediaQuery.addEventListener('change', this.onMediaQueryChange);
    else this.mediaQuery.addListener(this.onMediaQueryChange);
  },
  beforeUnmount() {
    if (this.mediaQuery) {
      if (this.mediaQuery.removeEventListener) this.mediaQuery.removeEventListener('change', this.onMediaQueryChange);
      else this.mediaQuery.removeListener(this.onMediaQueryChange);
    }
  },
  methods: {
    // CP-143: ON-state CTA from the per-line landing tier.
    compareCta(item) {
      return tierCtaLabel(item && item.affiliate_landing_tier);
    },
    // CP-141: CTA only when the flag is on and CruiseDirect sells the line.
    canBook(line) {
      return canBookLine(this.affiliateEnabled, line);
    },
    async fetchDestinations() {
      try {
        const response = await apiService.getDestinations();
        if (response.data && Array.isArray(response.data)) {
          // CP-34: destinations are {id, label}; keep API order (FILTER_ORDER)
          this.destinations = normalizeCanonicalDestinations(response.data);
        } else {
          console.error('Unexpected API response format:', response);
        }
      } catch (error) {
        console.error('Error fetching destinations:', error);
      }
    },
    async generateComparison() {
      this.loading = true;
      this.hasAttemptedComparison = true;
      this.error = null;
      
      try {
        const params = {
          cabin_type: this.selectedCabinType
        };
        if (this.selectedDestination) {
          params.destination = this.selectedDestination;
        }
        if (this.selectedDuration) {
          if (this.selectedDuration === '15+') {
            params.min_duration = 15;
          } else {
            const [min, max] = this.selectedDuration.split('-');
            params.min_duration = parseInt(min, 10);
            params.max_duration = parseInt(max, 10);
          }
        }

        const response = await apiService.getCruiseLineComparison(params);
        const comparison = (response.data && response.data.comparison) || [];
        this.comparisonData = comparison.map(item => ({
          cruiseLine: item.cruiseLine,
          avgPrice: item.avgPrice == null ? null : Number(item.avgPrice),
          minPrice: item.minPrice == null ? null : Number(item.minPrice),
          maxPrice: item.maxPrice == null ? null : Number(item.maxPrice),
          count: Number(item.count) || 0,
          catalogCount: Number(item.catalogCount) || 0,
          omissionReason: item.omissionReason || null,
          countNote: item.countNote || null
        }));
        this.updateChartData();
      } catch (error) {
        console.error('Error generating comparison:', error);
        this.comparisonData = [];
        this.chartData = null;
        this.error = 'Unable to generate comparison. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    formatMoney(value) {
      return formatCompareMoney(value);
    },
    updateChartData() {
      const priced = this.pricedComparison;
      const labels = priced.map(item => item.cruiseLine);
      const data = priced.map(item => item.avgPrice);
      const colors = tealSequential(priced.length);
      
      this.chartData = {
        labels,
        datasets: [{
          label: 'Average Price',
          data,
          backgroundColor: colors,
          borderColor: colors.map(c => c.replace(/[\d.]+\)$/, '1)')),
          borderWidth: 1,
          borderRadius: 4
        }]
      };
    },
    getComparisonSubtitle() {
      // Cabin options already use the consumer wording as their value
      // (Interior, Oceanview, Balcony, Suite). Destination options store the
      // API id; the subtitle must use the same label the dropdown shows.
      let subtitle = `${this.selectedCabinType} cabins`;
      
      if (this.selectedDestination) {
        subtitle += ` to ${labelForDestinationId(this.destinations, this.selectedDestination)}`;
      }
      
      if (this.selectedDuration) {
        if (this.selectedDuration === '15+') {
          subtitle += `, 15+ days`;
        } else {
          subtitle += `, ${this.selectedDuration} days`;
        }
      }
      
      return subtitle;
    },
    // CP-17: line-level affiliate redirect (comparison items are per-line
    // aggregates with no single cruise_id — backend resolves the line landing).
    affiliateUrl(item) {
      const line = encodeURIComponent(item.cruiseLine || '');
      return `/api/affiliate/redirect?partner=cruisedirect&source=compare&line=${line}`;
    },
    handleAffiliateClick(item) {
      notifyAffiliateClick({ partnerName: 'CruiseDirect', cruiseLine: item.cruiseLine });
    }
  }
};
</script>

<style scoped>
.cruise-comparison {
  margin: 0;
  padding: 0;
}

h2, h3 {
  text-align: center;
  color: var(--color-text);
  font-family: var(--font-heading);
}

h2 {
  margin-bottom: var(--space-5);
}

h3 {
  margin-top: var(--space-6);
  margin-bottom: var(--space-3);
}

.comparison-subtitle {
  text-align: center;
  color: var(--color-text-muted);
  font-style: italic;
  margin-bottom: var(--space-3);
}

.comparison-omitted,
.omission-reason,
.line-card-catalog,
.count-note,
.count-header-note {
  color: var(--color-text-muted);
  font-size: var(--text-small);
  line-height: 1.4;
}

.count-note {
  display: block;
  font-weight: 400;
}

.count-header-note {
  font-weight: 400;
}

.comparison-omitted {
  text-align: center;
  margin: 0 auto var(--space-5);
  max-width: 42rem;
}

.omission-reason {
  font-style: italic;
}

.line-card-catalog {
  margin: var(--space-3) 0 0;
}

/* Phone: stacked full-width filters */
.comparison-filters {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-5);
  padding: var(--space-4);
  background-color: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  border: 1px solid var(--cs-card-border);
}

.filter-group {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.filter-group label {
  margin-bottom: var(--space-2);
  font-weight: 600;
  color: var(--color-text-muted);
  font-size: var(--text-small);
}

.filter-group select {
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--text-body);
  background: var(--color-surface);
  color: var(--color-text);
  min-height: var(--touch-min);
  width: 100%;
}

.filter-group select:focus {
  outline: none;
  border-color: var(--cs-signal-teal);
  box-shadow: 0 0 0 2px var(--cs-signal-mist);
}

.compare-button {
  width: 100%;
  min-height: var(--touch-min);
  padding: var(--space-3) var(--space-5);
  background-color: var(--cs-fill);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-weight: 600;
  font-size: var(--text-body);
  transition: background var(--transition-normal);
}

.compare-button:hover:not(:disabled) {
  background-color: var(--cs-fill-hover);
}

.compare-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.chart-container {
  height: 360px;
  margin-bottom: var(--space-6);
  background: var(--cs-card);
  border-radius: var(--radius-lg);
  padding: var(--space-3);
  border: 1px solid var(--cs-card-border);
}

.chart-container.chart-horizontal {
  height: auto;
  min-height: 320px;
  height: max(320px, calc(var(--compare-rows, 6) * 48px));
}

.desktop-table {
  display: none;
  overflow-x: auto;
}

.comparison-cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.line-card {
  background: var(--cs-card);
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
  box-shadow: var(--shadow-card);
}

.line-card-title {
  margin: 0 0 var(--space-3);
  font-family: var(--font-heading);
  font-size: 18px;
  color: var(--cs-ink);
}

.line-card-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
  margin: 0;
}

.line-card-stats dt {
  font-size: var(--text-caption);
  color: var(--cs-muted);
  margin: 0;
}

.line-card-stats dd {
  margin: 2px 0 0;
  font-weight: 600;
  color: var(--cs-price);
  font-size: var(--text-body);
}

/* CP-17: per-line action bar (Explore Line Cruises + affiliate pill) */
.line-card-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-top: var(--space-4);
}

.line-card-explore {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--cs-signal-teal);
  font-weight: 600;
  font-size: var(--text-small);
  text-decoration: none;
  min-height: var(--touch-min);
}

.line-card-explore:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.line-card-affiliate {
  flex: 0 0 auto;
}

.compare-affiliate-cta {
  white-space: nowrap;
}

.compare-book-quiet {
  display: inline;
  padding: 0;
  min-height: 0;
  font-size: var(--text-caption);
  color: var(--cs-muted);
  font-weight: 500;
}

/* CP-17: table/card footer affiliate disclosure strip */
.comparison-affiliate-note {
  font-size: 12px;
  color: var(--cs-muted);
  line-height: 1.4;
  margin-top: var(--space-4);
}

.comparison-affiliate-note i {
  color: var(--cs-signal-teal);
  margin-right: 4px;
}

.comparison-affiliate-note a {
  color: var(--cs-signal-teal);
  text-decoration: underline;
  text-underline-offset: 2px;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: var(--space-5);
}

th, td {
  padding: var(--space-3) var(--space-4);
  text-align: left;
  border-bottom: 1px solid var(--color-border);
}

th {
  background-color: var(--cs-signal-mist);
  font-weight: 600;
  color: var(--color-text-muted);
}

tr:hover {
  background-color: var(--color-background);
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: var(--space-6) 0;
}

.loading-indicator p {
  color: var(--color-text-muted);
  margin-top: var(--space-4);
}

.spinner {
  border: 4px solid var(--color-border-light);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border-top-color: var(--cs-signal-teal);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-results {
  text-align: center;
  padding: var(--space-6);
  color: var(--color-text-muted);
}

@media (min-width: 768px) {
  .comparison-filters {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
  }

  .filter-group {
    flex: 1;
    min-width: 160px;
    width: auto;
  }

  .compare-button {
    width: auto;
    align-self: flex-end;
    min-width: 140px;
  }

  .desktop-table {
    display: block;
  }

  .comparison-cards {
    display: none;
  }

  .chart-container {
    height: 400px;
  }
}
</style>
